import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Linkedin, Users as UsersIcon } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { BRAND_ASSETS } from "@/lib/brandAssets";

export default function Team() {
  const [members, setMembers] = useState(null); // null = loading
  useEffect(() => {
    api.get("/team")
      .then((r) => setMembers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMembers([]));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="team-page">
      <Navbar />
      <main>
        {/* hero */}
        <section className="relative overflow-hidden border-b border-kb-divider">
          <div aria-hidden className="absolute inset-0 kb-kinetic-hex opacity-60" />
          <div aria-hidden className="absolute -right-24 top-10 w-[420px] h-[420px] opacity-[0.08]"
            style={{ background: "radial-gradient(closest-side, #7A44FC, transparent 70%)" }} />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">THE TEAM</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-kb-ink mt-4 leading-[1.05] max-w-3xl">
              Researchers, <em className="italic text-kb-purple">mentors, builders</em>.
            </h1>
            <p className="mt-6 text-lg text-kb-ink-muted max-w-2xl">
              KhojByte is a small, opinionated team of academics and engineers — building the
              research operating system we always wished existed.
            </p>
          </div>
        </section>

        {/* photo strip */}
        <section className="bg-kb-subtle border-b border-kb-divider">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-6">
            <img src={BRAND_ASSETS.teamHandshake} alt="Partnership" className="rounded-md object-cover h-56 w-full" />
            <img src={BRAND_ASSETS.teamLookingLaptop} alt="Researchers collaborating" className="rounded-md object-cover h-56 w-full" />
            <img src={BRAND_ASSETS.companyBoard} alt="Company announcement" className="rounded-md object-cover h-56 w-full" />
          </div>
        </section>

        {/* dynamic team grid */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          {members === null ? (
            <div className="kb-mono text-xs text-kb-ink-muted">Loading team…</div>
          ) : members.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-kb-divider rounded-md bg-white" data-testid="team-empty">
              <UsersIcon className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
              <h3 className="font-serif text-xl text-kb-ink mt-4">Team roster coming soon</h3>
              <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
                Our editors are finalising the public team profiles. Meanwhile, learn what we do on
                the <Link to="/about" className="text-kb-purple hover:underline">About</Link> page.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="team-grid">
              {members.map((m) => (
                <div key={m.role_id} className="kb-card p-5 kb-tilt" data-testid={`team-member-${m.user_id}`}>
                  <div className="h-44 w-full bg-kb-subtle rounded-md overflow-hidden mb-4 flex items-center justify-center">
                    {m.picture ? (
                      <img src={m.picture} alt={m.name} className="object-cover w-full h-full" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-white border border-kb-divider text-kb-purple flex items-center justify-center text-2xl font-bold">
                        {m.initials}
                      </div>
                    )}
                  </div>
                  <div className="font-serif text-lg text-kb-ink">{m.name}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-[0.18em] text-kb-purple mt-1">{m.role_name}</div>
                  {m.affiliation && (
                    <div className="text-[11px] text-kb-ink-light mt-1">{m.affiliation}</div>
                  )}
                  <p className="text-sm text-kb-ink-muted mt-3 leading-relaxed line-clamp-4">
                    {m.description || m.bio || ""}
                  </p>
                  <div className="mt-4 flex gap-3 text-kb-ink-muted">
                    <a href="#" aria-label="Email" className="hover:text-kb-purple"><Mail className="w-4 h-4" /></a>
                    <a href="#" aria-label="LinkedIn" className="hover:text-kb-purple"><Linkedin className="w-4 h-4" /></a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* join CTA */}
        <section className="bg-kb-dark text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mb-3">JOIN US</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight">
                Want to mentor or join the lab?
              </h2>
              <p className="mt-4 text-white/70 max-w-2xl">
                We are always looking for researchers, reviewers and engineers who care about
                ethical publication and Nepal's research ecosystem.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end gap-3">
              <Link to="/about#contact" className="kb-btn-outline border-white/30 text-white hover:bg-white/10" data-testid="team-cta-contact">
                Contact us
              </Link>
              <Link to="/onboarding/role" className="kb-btn-primary" data-testid="team-cta-join">
                Apply <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
